import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { MenuService } from '../services/menu.service';
import { UserAuthService } from '../services/user-auth.service';
import { ManagerialOperationsService } from '../services/managerial-operations.service';

/**
 * Sends the app root ('/') to the org's pinned landing page instead of a fixed
 * '/app'.
 *
 * Why a guard and not `redirectTo`: Angular resolves `redirectTo` during route
 * RECOGNITION, before guards run — so a redirect route can never consult the
 * org config. The root route therefore has to activate something in order to
 * decide, and this guard answers with a UrlTree.
 *
 * Previously the landing was only ever resolved on the sign-in submit path, so
 * an already-signed-in user who re-opened the tab or refreshed always got
 * Dashboard; only a full logout/login honoured the setting. Users do not log in
 * daily, so the setting was effectively invisible to them.
 *
 * Deliberately scoped to the BARE ROOT. '/app' and every deep link still open
 * exactly what was asked for, so bookmarks, shared links and browser
 * tab-restore keep working.
 *
 * Fail-safe in three ways, each landing on '/app' — the pre-existing behaviour:
 *   - Not a browser (SSR): auth lives in session/localStorage, which does not
 *     exist on the server, and reading it there THROWS. The server cannot know
 *     who the user is anyway, so it defers; the client re-runs this guard on
 *     bootstrap and performs the real redirect.
 *   - Not signed in: '/app' is guarded by AuthGuard, which routes to login —
 *     exactly what the old redirect did. Never call getFirstMenuLink() here:
 *     an anonymous user resolves to roleId 0, no menu item matches role 0, and
 *     it would return '/no-access' instead of the login page.
 *   - Any error: '/app'.
 */
@Injectable({ providedIn: 'root' })
export class LandingRedirectGuard implements CanActivate {
  constructor(
    private router: Router,
    private menuService: MenuService,
    private userAuthService: UserAuthService,
    private managerOperations: ManagerialOperationsService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  canActivate(): Observable<boolean | UrlTree> {
    const appUrl = this.router.parseUrl('/app');

    if (!isPlatformBrowser(this.platformId)) {
      return of(appUrl);
    }

    let signedIn = false;
    try {
      signedIn =
        this.managerOperations.isLoggedIn() || this.userAuthService.isLoggedIn();
    } catch (e) {
      return of(appUrl);
    }
    if (!signedIn) {
      return of(appUrl);
    }

    // getFirstMenuLink() already honours the pinned landing ONLY when it is
    // visible to this user, and otherwise falls back to their first visible
    // menu — so the safety rule is inherited here rather than re-implemented.
    return this.menuService.getFirstMenuLink().pipe(
      map((link) => this.router.parseUrl(link ? link : '/app')),
      catchError(() => of(appUrl)),
    );
  }
}
